/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * The variations table's filter state, and what it narrows.
 *
 * ONE module for two consumers. The table renders the controls; the specifications panel recomputes
 * its union over whatever the filters leave visible. They sit in different branches of the tree and
 * share nothing but the URL, so the rule for reading that URL has to live somewhere neither owns —
 * written twice, the two would drift and the page would state a value the filter had excluded.
 */
import type { VariationColumn, VariationsTableModel, VariationRow } from './variations-table';

/**
 * Prefix on every filter's search parameter.
 *
 * REQUIRED, not cosmetic. `useSelectedVariations` reads the master's variation attribute ids straight
 * from the search params to resolve which variant the gallery shows, and the attributes this table
 * filters on are frequently the same ids. An unprefixed `shaft-eyelet=Trunnion` would therefore read
 * as a variant SELECTION and swap the page image as a side effect of narrowing a list.
 */
export const FILTER_PARAM_PREFIX = 'f_';

/** Merchant attributes carry `c_`; the URL does not need to. */
export function filterParamName(attributeId: string): string {
    return `${FILTER_PARAM_PREFIX}${attributeId.replace(/^c_/, '')}`;
}

/** One filter that is set to a value the set actually contains. */
export interface ActiveVariationFilter {
    column: VariationColumn;
    value: string;
}

/**
 * Reads the filters that are both set and meaningful.
 *
 * A value the set does not contain is DROPPED rather than applied. A hand-edited or stale address
 * would otherwise filter on something no row could match, emptying the table with no way to tell that
 * from a genuine empty result.
 *
 * @param model - The derived table model
 * @param params - The current search parameters
 * @returns The filters to apply, in the model's column order
 */
export function readActiveFilters(model: VariationsTableModel, params: URLSearchParams): ActiveVariationFilter[] {
    return model.filters.flatMap((column) => {
        const value = params.get(filterParamName(column.attributeId));

        return value !== null && column.values.includes(value) ? [{ column, value }] : [];
    });
}

/**
 * The rows surviving every active filter.
 *
 * @param model - The derived table model
 * @param filters - The filters to apply
 * @returns The visible rows, in the model's order
 */
export function filterVariationRows(
    model: VariationsTableModel,
    filters: readonly ActiveVariationFilter[]
): VariationRow[] {
    return model.rows.filter((row) => filters.every(({ column, value }) => row.values[column.attributeId] === value));
}

/**
 * The values one filter may offer: those still present once every OTHER filter is applied.
 *
 * This is what makes an empty result unreachable through the controls. Offering an attribute's full
 * value list regardless of the other selections lets a shopper assemble a combination no variant has —
 * pick a compression tune, then a rebound tune that never ships with it, and the table empties.
 *
 * Excluding the filter's OWN value is what keeps it changeable: measured against itself, the only value
 * ever available would be the one already chosen.
 *
 * Order comes from the column, not from this walk, so options keep their catalogue sequence instead of
 * being reshuffled by whichever sibling is active.
 *
 * @param model - The derived table model
 * @param filters - The currently active filters
 * @param column - The filter whose options are being computed
 * @returns The selectable values, in the column's own order
 */
export function reachableValues(
    model: VariationsTableModel,
    filters: readonly ActiveVariationFilter[],
    column: VariationColumn
): string[] {
    const others = filters.filter((entry) => entry.column.attributeId !== column.attributeId);
    const reachable = new Set(filterVariationRows(model, others).map((row) => row.values[column.attributeId]));

    return column.values.filter((value) => reachable.has(value));
}

/**
 * Applies a filter change to a copy of the current parameters.
 *
 * Returns the next parameters rather than navigating, so the caller decides how the change reaches the
 * URL and this module stays free of router concerns.
 *
 * @param params - The current search parameters
 * @param attributeId - The attribute whose filter changed
 * @param value - The chosen value; empty clears the filter
 * @returns The parameters to navigate to
 */
export function withFilter(params: URLSearchParams, attributeId: string, value: string): URLSearchParams {
    const next = new URLSearchParams(params);

    if (value) next.set(filterParamName(attributeId), value);
    else next.delete(filterParamName(attributeId));

    return next;
}

/**
 * Clears every filter the model can set, leaving unrelated parameters alone.
 *
 * Deletes by the model's filters rather than by scanning for the prefix, so a parameter belonging to
 * another surface that happens to share it is not swept away.
 *
 * @param params - The current search parameters
 * @param model - The derived table model
 * @returns The parameters to navigate to
 */
export function withoutFilters(params: URLSearchParams, model: VariationsTableModel): URLSearchParams {
    const next = new URLSearchParams(params);

    for (const column of model.filters) next.delete(filterParamName(column.attributeId));

    return next;
}
